<template>
  <div class="painel-customer-success">
    <!-- Cabeçalho e Select de Loja -->
    <b-card class="mb-4 shadow-sm">
      <h4 class="mb-3">
        <feather-icon
          icon="LayoutIcon"
          class="mr-50"
        />
        Painel Customer Success
      </h4>
      <b-row align-v="end">
        <b-col
          cols="12"
          md="6"
          lg="5"
        >
          <b-form-group
            label="Loja"
            label-for="store-select-cs"
          >
            <v-select
              id="store-select-cs"
              v-model="storeSelected"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              label="name"
              :options="storesOptions"
              :reduce="s => s"
              placeholder="Busque ou selecione uma loja..."
              :filterable="true"
              :clearable="false"
              @input="onStoreChange"
            >
              <template #option="{ name }">
                {{ name }}
              </template>
              <template #selected-option="{ name }">
                {{ name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <template v-if="!storeSelected">
      <b-card class="text-center py-5">
        <feather-icon
          icon="StoreIcon"
          size="64"
          class="text-muted mb-3"
        />
        <p class="text-muted mb-0">
          Selecione uma loja acima para visualizar as métricas.
        </p>
      </b-card>
    </template>

    <template v-else>
      <!-- Loading geral -->
      <div
        v-if="loadingGeral"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">
          Carregando dados da loja...
        </p>
      </div>

      <template v-else>
        <!-- Cluster: Notificações -->
        <b-card class="mb-4 shadow-sm cluster-card">
          <h5 class="mb-3 text-primary">
            <feather-icon
              icon="BellIcon"
              class="mr-50"
            />
            Notificações
          </h5>
          <div
            v-if="loadingNotif"
            class="text-center py-3"
          >
            <b-spinner
              small
              variant="primary"
            />
          </div>
          <template v-else>
            <b-row>
              <b-col md="3">
                <div class="mb-2">
                  <small class="text-muted d-block">Ativas / Desativadas</small>
                  <span class="font-weight-bold text-success">{{ notifAtivas }}</span>
                  <span class="text-muted"> ativas</span>
                  <span class="mx-1">·</span>
                  <span class="font-weight-bold text-secondary">{{ notifDesativadas }}</span>
                  <span class="text-muted"> desativadas</span>
                </div>
              </b-col>
              <b-col md="3">
                <div class="mb-2">
                  <small class="text-muted d-block">Notificações enviadas (últimos 30 dias)</small>
                  <span class="font-weight-bold">{{ totalNotifEnviadas }}</span>
                </div>
              </b-col>
              <b-col md="3">
                <div class="mb-2">
                  <small class="text-muted d-block">Conversões carrinho abandonado</small>
                  <span class="font-weight-bold text-success">{{ conversoesCarrinho }}</span>
                </div>
              </b-col>
              <b-col md="3">
                <div class="mb-2">
                  <small class="text-muted d-block">Última notificação enviada</small>
                  <span class="font-weight-bold">{{ ultimaNotificacaoEnviada }}</span>
                </div>
              </b-col>
            </b-row>
            <div class="mt-2 d-flex align-items-center flex-wrap">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-2 mb-2"
                :disabled="!rawReportNotif || totalNotifEnviadas === 0"
                @click="historicoNotifPagina = 1; modalHistoricoNotifVisivel = true"
              >
                <feather-icon
                  icon="ListIcon"
                  size="14"
                  class="mr-50"
                />
                Histórico detalhado
              </b-button>
            </div>
            <div class="mt-2">
              <small class="text-muted">Lista de notificações configuradas</small>
              <b-list-group class="mt-1">
                <b-list-group-item
                  v-for="(n, idx) in notificacoesLista"
                  :key="idx"
                  class="py-3"
                >
                  <div class="d-flex justify-content-between align-items-center flex-wrap">
                    <div class="flex-grow-1 mr-2">
                      <div class="font-weight-bold">
                        {{ nomeStatusNotif(n.status_id) }} ({{ nomePaymentNotif(n.payment_id) }})
                      </div>
                      <div
                        v-if="n.template_name"
                        class="small text-info mt-1"
                      >
                        Template: <strong>{{ n.template_name }}</strong>
                      </div>
                    </div>
                    <div class="d-flex align-items-center flex-wrap">
                      <b-badge
                        v-if="ultimas10ComFalha(n)"
                        variant="danger"
                        class="mr-2"
                      >
                        ⚠️ Alerta
                      </b-badge>
                      <b-button
                        v-if="n.template_name || n.body"
                        size="sm"
                        variant="outline-primary"
                        class="mr-2"
                        @click="abrirModalTemplateNotif(n)"
                      >
                        Ver template
                      </b-button>
                      <b-badge :variant="n.ativo ? 'success' : 'secondary'">
                        {{ n.ativo ? 'Ativa' : 'Desativada' }}
                      </b-badge>
                    </div>
                  </div>
                </b-list-group-item>
                <b-list-group-item
                  v-if="notificacoesLista.length === 0"
                  class="text-muted"
                >
                  Nenhuma notificação configurada
                </b-list-group-item>
              </b-list-group>
            </div>
          </template>
        </b-card>

        <!-- Cluster: Campanhas Segmentadas -->
        <b-card class="mb-4 shadow-sm cluster-card">
          <h5 class="mb-3 text-primary">
            <feather-icon
              icon="SendIcon"
              class="mr-50"
            />
            Campanhas Segmentadas
          </h5>
          <div
            v-if="loadingCampanhas"
            class="text-center py-3"
          >
            <b-spinner
              small
              variant="primary"
            />
          </div>
          <template v-else>
            <b-row class="mb-3">
              <b-col md="3">
                <small class="text-muted d-block">Campanhas enviadas</small>
                <span class="font-weight-bold">{{ estatisticasCampanhas.total_campanhas || 0 }}</span>
              </b-col>
              <b-col md="3">
                <small class="text-muted d-block">Faturamento total</small>
                <span class="font-weight-bold">{{ formatarMoeda(estatisticasCampanhas.valor_total_conversoes) }}</span>
              </b-col>
              <b-col md="3">
                <small class="text-muted d-block">Última criada</small>
                <span class="font-weight-bold">{{ ultimaCampanhaCriada }}</span>
              </b-col>
              <b-col md="3">
                <small class="text-muted d-block">Última enviada</small>
                <span class="font-weight-bold">{{ ultimaCampanhaEnviada }}</span>
              </b-col>
            </b-row>
            <div v-if="campanhasSegmentadas.length > 0">
              <small class="text-muted d-block mb-2">Campanhas — role horizontalmente para ver mais</small>
              <div class="campanhas-scroll-wrapper">
                <div class="campanhas-scroll">
                  <b-card
                    v-for="c in campanhasSegmentadas"
                    :key="c.id"
                    class="campanha-card-scroll border"
                  >
                    <h6
                      class="mb-2 text-truncate"
                      :title="c.name"
                    >
                      {{ c.name }}
                    </h6>
                    <div class="small mb-2">
                      <div><span class="text-muted">Criada:</span> {{ formatarDataHora(c.created_at) }}</div>
                      <div><span class="text-muted">Agendada:</span> {{ formatarDataHora(c.send_date) }}</div>
                      <div>
                        <span class="text-muted">Enviada:</span>
                        <b-badge
                          v-if="campanhaFoiEnviada(c)"
                          variant="success"
                        >
                          Sim
                        </b-badge>
                        <b-badge
                          v-else
                          variant="secondary"
                        >
                          Não
                        </b-badge>
                        <span
                          v-if="campanhaFoiEnviada(c) && (c.last_sent_at || c.send_date)"
                          class="ml-1 small"
                        >
                          {{ formatarDataHora(c.last_sent_at || c.send_date) }}
                        </span>
                      </div>
                    </div>
                    <div class="small border-top pt-2">
                      <div><span class="text-muted">Custo:</span> {{ metricasPorCampanha[c.id] ? formatarMoeda(metricasPorCampanha[c.id].custo) : '-' }}</div>
                      <div><span class="text-muted">ROI:</span> {{ metricasPorCampanha[c.id] && metricasPorCampanha[c.id].roi != null ? (metricasPorCampanha[c.id].roi + '%') : '-' }}</div>
                      <div><span class="text-muted">Faturamento:</span> {{ metricasPorCampanha[c.id] ? formatarMoeda(metricasPorCampanha[c.id].valor_total) : '-' }}</div>
                    </div>
                  </b-card>
                </div>
              </div>
            </div>
            <p
              v-else
              class="text-muted mb-0"
            >
              Nenhuma campanha segmentada.
            </p>
          </template>
        </b-card>

        <!-- Cluster: Campanhas Inteligentes -->
        <b-card class="mb-4 shadow-sm cluster-card">
          <h5 class="mb-3 text-primary">
            <feather-icon
              icon="ZapIcon"
              class="mr-50"
            />
            Campanhas Inteligentes
          </h5>
          <div
            v-if="loadingInteligentes"
            class="text-center py-3"
          >
            <b-spinner
              small
              variant="primary"
            />
          </div>
          <template v-else>
            <b-row>
              <!-- Recomendações -->
              <b-col
                md="6"
                class="mb-3"
              >
                <b-card class="h-100 border-primary">
                  <h6 class="mb-2">
                    <feather-icon
                      icon="PackageIcon"
                      size="18"
                      class="mr-50"
                    />
                    Recomendações
                  </h6>
                  <div v-if="statusRecomendacao">
                    <b-badge
                      :variant="(statusRecomendacao.active || statusRecomendacao.ativo) ? 'success' : 'secondary'"
                      class="mb-2"
                    >
                      {{ (statusRecomendacao.active || statusRecomendacao.ativo) ? 'Ativa' : 'Inativa' }}
                    </b-badge>
                    <div
                      v-if="metricasRecomendacao && conversoesRecomendacaoFormatadas.length"
                      class="small"
                    >
                      <div class="font-weight-bold mb-1">
                        Conversões (recomendações)
                      </div>
                      <div
                        v-for="item in conversoesRecomendacaoFormatadas"
                        :key="item.label"
                        class="d-flex justify-content-between"
                      >
                        <span class="text-muted">{{ item.label }}</span>
                        <span>{{ item.value }}</span>
                      </div>
                    </div>
                  </div>
                  <p
                    v-else
                    class="text-muted small mb-0"
                  >
                    Não configurado ou sem dados
                  </p>
                </b-card>
              </b-col>
              <!-- Aniversariantes -->
              <b-col
                md="6"
                class="mb-3"
              >
                <b-card class="h-100 border-info">
                  <h6 class="mb-2">
                    <feather-icon
                      icon="GiftIcon"
                      size="18"
                      class="mr-50"
                    />
                    Aniversariantes
                  </h6>
                  <div v-if="campanhaAniversarioAtiva">
                    <b-badge
                      variant="success"
                      class="mb-2"
                    >
                      Ativa
                    </b-badge>
                    <p class="small mb-1">
                      {{ campanhaAniversarioAtiva.name }}
                    </p>
                    <div
                      v-if="metricasAniversario"
                      class="small"
                    >
                      <div><span class="text-muted">Mensagens enviadas:</span> {{ metricasAniversario.total_sent || 0 }}</div>
                      <div><span class="text-muted">Conversões:</span> {{ metricasAniversario.conversions_count != null ? metricasAniversario.conversions_count : '-' }}</div>
                      <div><span class="text-muted">Valor:</span> {{ formatarMoeda(metricasAniversario.valor_total) }}</div>
                    </div>
                  </div>
                  <p
                    v-else
                    class="text-muted small mb-0"
                  >
                    Nenhuma campanha de aniversário ativa
                  </p>
                </b-card>
              </b-col>
            </b-row>
          </template>
        </b-card>

        <!-- Modal: Template da notificação -->
        <b-modal
          :visible="modalTemplateNotifVisivel"
          title="Template da notificação"
          size="md"
          ok-only
          ok-title="Fechar"
          @hide="modalTemplateNotifVisivel = false"
        >
          <div v-if="notifModalSelecionada">
            <p class="mb-2">
              <strong>{{ nomeStatusNotif(notifModalSelecionada.status_id) }}</strong>
              — {{ notifModalSelecionada.template_name || 'Sem nome' }}
            </p>
            <div
              class="border rounded p-3 bg-light small text-break"
              style="white-space: pre-wrap;"
            >
              {{ notifModalSelecionada.body || 'Sem conteúdo.' }}
            </div>
          </div>
        </b-modal>

        <!-- Modal: Histórico detalhado de notificações (últimos 30 dias) -->
        <b-modal
          :visible="modalHistoricoNotifVisivel"
          title="Histórico detalhado (últimos 30 dias)"
          size="lg"
          ok-only
          ok-title="Fechar"
          @hide="modalHistoricoNotifVisivel = false"
        >
          <p class="small text-muted mb-2">
            Período: {{ periodoNotif.dataMin }} a {{ periodoNotif.dataMax ? periodoNotif.dataMax.replace(' 23:59:59', '') : '' }}
          </p>
          <b-table
            :items="historicoNotificacoesPaginated"
            :fields="historicoNotifCampos"
            striped
            small
            responsive
            show-empty
            empty-text="Nenhuma notificação no período."
            head-variant="light"
            sort-by="data"
            sort-desc
          >
            <template #cell(ativa)="data">
              <b-badge :variant="notificacaoAtivaPorStatus(data.item.status_id) ? 'success' : 'secondary'">
                {{ notificacaoAtivaPorStatus(data.item.status_id) ? 'Sim' : 'Não' }}
              </b-badge>
            </template>
            <template #cell(envio)="data">
              <b-badge :variant="data.item.envio === 'Enviado' ? 'success' : 'secondary'">
                {{ data.item.envio }}
              </b-badge>
            </template>
          </b-table>
          <div
            v-if="historicoNotificacoesUnificado.length > 10"
            class="d-flex justify-content-between align-items-center mt-2 flex-wrap"
          >
            <span class="small text-muted">
              {{ (historicoNotifPagina - 1) * 10 + 1 }} a {{ Math.min(historicoNotifPagina * 10, historicoNotificacoesUnificado.length) }} de {{ historicoNotificacoesUnificado.length }}
            </span>
            <b-pagination
              v-model="historicoNotifPagina"
              :total-rows="historicoNotificacoesUnificado.length"
              :per-page="10"
              size="sm"
              align="right"
              first-number
              last-number
            />
          </div>
        </b-modal>

        <!-- Cluster: Central Atendimento (placeholder) -->
        <b-card class="mb-4 shadow-sm cluster-card border-secondary">
          <h5 class="mb-3 text-secondary">
            <feather-icon
              icon="MessageCircleIcon"
              class="mr-50"
            />
            Central de Atendimento
          </h5>
          <p class="text-muted mb-0">
            Conteúdo a ser preenchido em breve.
          </p>
        </b-card>
      </template>
    </template>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BSpinner, BListGroup, BListGroupItem, BBadge, BButton, BModal, BTable, BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { getStoresUserActived } from '@/functions/basics/stores'
import { getUserData } from '@/auth/utils'
import { listarNotificacoes, STATUS_NOTIFICACOES, METODOS_PAGAMENTO } from '@/functions/basics/notifications-config'
import report from '@/functions/Report/notification'
import { listarCampanhas, buscarEstatisticasCampanhas, buscarMetricasCampanha } from '@/functions/basics/campaign-oficial'
import { verificarStatusRecomendacoes, buscarMetricasRecomendacoes } from '@/functions/basics/recommendations'
import { listarCampanhasAniversario, buscarCampanhaAniversarioAtiva, buscarMetricasConversaoAniversario } from '@/functions/basics/birthday-campaigns'

const STATUS_LIST = {
  1: 'Aprovado',
  2: 'Aguardando Pagamento',
  3: 'Cancelada',
  4: 'Transporte',
  5: 'Entregue',
  6: 'Boleto Vencido',
  7: 'Aprovado Parcialmente',
  8: 'Carrinho Abandonado',
  9: 'Aprovado e Integrado',
  10: 'Transporte interno',
  11: 'Cashback',
}

export default {
  name: 'PainelCustomerSuccess',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BSpinner,
    BListGroup,
    BListGroupItem,
    BBadge,
    BButton,
    BModal,
    BTable,
    BPagination,
    vSelect,
    FeatherIcon,
  },
  data() {
    return {
      storesList: [],
      storeSelected: null,
      loadingGeral: false,
      loadingNotif: false,
      loadingCampanhas: false,
      loadingInteligentes: false,
      // Notificações
      notificacoesLista: [],
      rawReportNotif: null,
      periodoNotif: { dataMin: '', dataMax: '' },
      // Campanhas segmentadas
      campanhasSegmentadas: [],
      estatisticasCampanhas: {},
      metricasPorCampanha: {},
      // Inteligentes
      statusRecomendacao: null,
      metricasRecomendacao: null,
      campanhaAniversarioAtiva: null,
      campanhasAniversario: [],
      metricasAniversario: null,
      modalTemplateNotifVisivel: false,
      notifModalSelecionada: null,
      modalHistoricoNotifVisivel: false,
      historicoNotifPagina: 1,
      historicoNotifCampos: [
        { key: 'data', label: 'Data', sortable: true },
        { key: 'tipo', label: 'Tipo', sortable: true },
        { key: 'status', label: 'Status' },
        { key: 'ativa', label: 'Ativa' },
        { key: 'nome', label: 'Nome' },
        { key: 'telefone', label: 'Telefone' },
        { key: 'referencia', label: 'Referência' },
        { key: 'envio', label: 'Enviado' },
      ],
    }
  },
  computed: {
    storesOptions() {
      return this.storesList || []
    },
    /** UUID da loja selecionada (suporta uuid, store_uuid, storeUuid ou id quando for string no formato UUID) */
    storeUuid() {
      return this.getStoreUuid(this.storeSelected)
    },
    notifAtivas() {
      return (this.notificacoesLista || []).filter(n => n.ativo).length
    },
    notifDesativadas() {
      return (this.notificacoesLista || []).filter(n => !n.ativo).length
    },
    totalNotifEnviadas() {
      if (!this.rawReportNotif) return 0
      const r = (this.rawReportNotif.report || []).length
      const c = (this.rawReportNotif.reportCarts || []).length
      const cb = (this.rawReportNotif.cashback || []).length
      return r + c + cb
    },
    conversoesCarrinho() {
      if (!this.rawReportNotif || !this.rawReportNotif.reportCarts || !this.rawReportNotif.report) return 0
      const carrinhos = this.rawReportNotif.reportCarts
      const aprovados = (this.rawReportNotif.report || []).filter(x => x.status_id === 1)
      if (!aprovados.length) return 0
      const oneDayMs = 24 * 60 * 60 * 1000
      const sevenDaysMs = 7 * oneDayMs
      let count = 0
      carrinhos.forEach(notif => {
        const notifTime = this.parseDate(notif.created_at || notif.data_formatted)
        if (!notifTime || notif.customer_id == null) return
        const inicio = notifTime + oneDayMs
        const fim = notifTime + sevenDaysMs
        const tem = aprovados.some(a => {
          const t = this.parseDate(a.created_at || a.data_formatted)
          return t && a.customer_id === notif.customer_id && t >= inicio && t <= fim
        })
        if (tem) count += 1
      })
      return count
    },
    /** Data/hora da última notificação enviada (últimos 30 dias) */
    ultimaNotificacaoEnviada() {
      if (!this.rawReportNotif) return '-'
      let maxTs = 0
      const report = this.rawReportNotif.report || []
      const carts = this.rawReportNotif.reportCarts || []
      const cashback = this.rawReportNotif.cashback || []
      report.forEach(r => {
        const t = this.parseDate(r.created_at || r.data_formatted)
        if (t && t > maxTs) maxTs = t
      })
      carts.forEach(c => {
        const t = this.parseDate(c.created_at || c.data_formatted)
        if (t && t > maxTs) maxTs = t
      })
      cashback.forEach(cb => {
        const t = this.parseDate(cb.created_at || cb.data_formatted)
        if (t && t > maxTs) maxTs = t
      })
      return maxTs ? this.formatarDataHora(maxTs) : '-'
    },
    /** Lista unificada do histórico de notificações (últimos 30 dias) para o modal */
    historicoNotificacoesUnificado() {
      if (!this.rawReportNotif) return []
      const report = this.rawReportNotif.report || []
      const carts = this.rawReportNotif.reportCarts || []
      const cashback = this.rawReportNotif.cashback || []
      const out = []
      report.forEach(r => {
        const dataStr = (r.created_at || r.data_formatted || '-').toString().trim()
        out.push({
          data: dataStr,
          _ts: this.parseDate(r.created_at || r.data_formatted) || 0,
          status_id: r.status_id,
          tipo: 'Transacional',
          status: STATUS_LIST[r.status_id] || `Status ${r.status_id}`,
          nome: r.name || '-',
          telefone: r.phone || '-',
          referencia: (r.order_id != null ? r.order_id : r.platform_id) || '-',
          envio: (r.message_id ? 'Enviado' : 'Não enviado'),
        })
      })
      carts.forEach(c => {
        const dataStr = (c.data_formatted || c.created_at || '-').toString().trim()
        out.push({
          data: dataStr,
          _ts: this.parseDate(c.data_formatted || c.created_at) || 0,
          status_id: 8,
          tipo: 'Carrinho abandonado',
          status: '-',
          nome: c.name || '-',
          telefone: c.phone || '-',
          referencia: (c.abandoned_cart_id != null ? `Carrinho #${c.abandoned_cart_id}` : '-'),
          envio: (c.message_id ? 'Enviado' : 'Não enviado'),
        })
      })
      cashback.forEach(cb => {
        const dataStr = (cb.data_formatted || cb.created_at || '-').toString().trim()
        out.push({
          data: dataStr,
          _ts: this.parseDate(cb.data_formatted || cb.created_at) || 0,
          status_id: 11,
          tipo: 'Cashback',
          status: '-',
          nome: cb.name || '-',
          telefone: cb.phone || '-',
          referencia: (cb.cashback_id != null ? `Cashback #${cb.cashback_id}` : '-'),
          envio: (cb.message_id ? 'Enviado' : 'Não enviado'),
        })
      })
      out.sort((a, b) => (b._ts || 0) - (a._ts || 0))
      return out
    },
    /** Fatia do histórico para a página atual (10 por página) */
    historicoNotificacoesPaginated() {
      const list = this.historicoNotificacoesUnificado || []
      const page = this.historicoNotifPagina || 1
      const start = (page - 1) * 10
      return list.slice(start, start + 10)
    },
    ultimaCampanhaCriada() {
      if (!this.campanhasSegmentadas.length) return '-'
      const sorted = [...this.campanhasSegmentadas].sort((a, b) => {
        const da = this.parseDate(a.created_at)
        const db = this.parseDate(b.created_at)
        return (db || 0) - (da || 0)
      })
      return this.formatarData(sorted[0].created_at)
    },
    ultimaCampanhaEnviada() {
      if (!this.campanhasSegmentadas.length) return '-'
      const comData = this.campanhasSegmentadas.filter(c => c.last_sent_at || c.send_date)
      if (!comData.length) return '-'
      const sorted = [...comData].sort((a, b) => {
        const da = this.parseDate(a.last_sent_at || a.send_date)
        const db = this.parseDate(b.last_sent_at || b.send_date)
        return (db || 0) - (da || 0)
      })
      return this.formatarData(sorted[0].last_sent_at || sorted[0].send_date)
    },
    conversoesRecomendacaoFormatadas() {
      const raw = this.metricasRecomendacao
      const data = raw && (raw.data || raw)
      let conv = (data && data.conversions) || (data && data.summary && data.summary.conversions)
      if (typeof conv === 'string') {
        try {
          conv = JSON.parse(conv)
        } catch (_) {
          return []
        }
      }
      if (!conv || typeof conv !== 'object') return []
      const labels = {
        total_clicks: 'Cliques totais',
        direct_purchases: 'Compras diretas',
        direct_revenue: 'Receita direta',
        incentive_purchases: 'Compras incentivadas',
        incentive_revenue: 'Receita incentivada',
        total_purchases: 'Total de compras',
        total_revenue: 'Receita total',
        click_through_rate: 'Taxa de cliques (%)',
        conversion_rate: 'Taxa de conversão (%)',
        avg_order_value: 'Ticket médio',
      }
      const revenueKeys = ['direct_revenue', 'incentive_revenue', 'total_revenue', 'avg_order_value']
      return Object.keys(conv).map(key => {
        let value = conv[key]
        if (revenueKeys.includes(key) && (typeof value === 'number' || (typeof value === 'string' && value !== ''))) {
          value = this.formatarMoeda(Number(value))
        } else if ((key.includes('rate') || key === 'click_through_rate' || key === 'conversion_rate') && typeof value === 'number') {
          value = `${Number(value).toFixed(2)}%`
        }
        return { label: labels[key] || key, value }
      })
    },
  },
  async created() {
    await this.carregarLojas()
  },
  methods: {
    async carregarLojas() {
      try {
        const userData = getUserData()
        let list = await getStoresUserActived(userData.mc_access_token)
        if (list && !Array.isArray(list) && list.data) list = list.data
        this.storesList = Array.isArray(list) ? list : []
        if (this.storesList.length === 1) {
          this.storeSelected = this.storesList[0]
          this.$nextTick(() => this.onStoreChange(this.storeSelected))
        }
      } catch (e) {
        console.error('Erro ao carregar lojas:', e)
        this.$bvToast.toast('Erro ao carregar lista de lojas.', { title: 'Erro', variant: 'danger', solid: true })
      }
    },
    onStoreChange(store) {
      const s = store || this.storeSelected
      if (!s) return
      const uuid = this.getStoreUuid(s)
      if (!uuid) {
        this.$bvToast.toast('Loja sem identificador (uuid). Entre em contato com o suporte.', { title: 'Aviso', variant: 'warning', solid: true })
        return
      }
      this.loadingGeral = true
      this.carregarTodosClusters()
        .catch(err => {
          console.error('Erro ao carregar clusters:', err)
          this.$bvToast.toast('Erro ao carregar dados da loja. Tente novamente.', { title: 'Erro', variant: 'danger', solid: true })
        })
        .finally(() => { this.loadingGeral = false })
    },
    /** Retorna uuid da loja (API pode enviar uuid, store_uuid, storeUuid ou id como string) */
    getStoreUuid(store) {
      if (!store || typeof store !== 'object') return null
      if (store.uuid) return store.uuid
      if (store.store_uuid) return store.store_uuid
      if (store.storeUuid) return store.storeUuid
      if (typeof store.id === 'string' && store.id.length >= 30) return store.id
      return null
    },
    getPeriodoPadrao() {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 30)
      const startStr = start.toISOString().slice(0, 10)
      const endStr = end.toISOString().slice(0, 10)
      return { dataMin: startStr, dataMax: `${endStr} 23:59:59` }
    },
    async carregarTodosClusters() {
      this.periodoNotif = this.getPeriodoPadrao()
      await Promise.all([
        this.carregarNotificacoes(),
        this.carregarCampanhasSegmentadas(),
        this.carregarCampanhasInteligentes(),
      ])
    },
    async carregarNotificacoes() {
      const uuid = this.storeUuid
      if (!uuid) return
      this.loadingNotif = true
      try {
        const [templates, rawReport] = await Promise.all([
          listarNotificacoes(uuid),
          report(this.periodoNotif.dataMin, this.periodoNotif.dataMax, uuid).catch(() => null),
        ])
        this.rawReportNotif = rawReport
        const list = (templates || []).map(t => ({
          status_id: t.status_id,
          payment_id: t.payment_id,
          ativo: t.ativo === 1 || t.ativo === true || t.is_active === 1 || t.is_active === true,
          template_name: t.template_name_meta || t.template_name || t.name || '',
          body: t.body || t.body_text || '',
        }))
        this.notificacoesLista = list
      } catch (e) {
        console.error('Erro notificações:', e)
        this.notificacoesLista = []
        this.rawReportNotif = null
      } finally {
        this.loadingNotif = false
      }
    },
    async carregarCampanhasSegmentadas() {
      const uuid = this.storeUuid
      if (!uuid) return
      this.loadingCampanhas = true
      try {
        const [campanhas, statsRaw] = await Promise.all([
          listarCampanhas(uuid),
          buscarEstatisticasCampanhas(uuid).catch(() => ({})),
        ])
        const campanhasList = (campanhas || []).filter(c => !c.deleted_at)
        this.campanhasSegmentadas = campanhasList
        // API pode retornar { data: { total_campanhas, valor_total_conversoes } }
        const stats = (statsRaw && statsRaw.data) ? statsRaw.data : (statsRaw || {})
        this.estatisticasCampanhas = stats
        this.metricasPorCampanha = {}
        // Buscar métricas de cada campanha (resposta pode vir em .data)
        for (const c of campanhasList) {
          try {
            let m = await buscarMetricasCampanha(uuid, c.id)
            if (m && m.data) m = m.data
            const conversoes = (m && m.conversoes) ? m.conversoes : {}
            const roiObj = (m && m.roi) ? m.roi : {}
            const valor = (conversoes.valor_total != null && conversoes.valor_total !== '') ? Number(conversoes.valor_total) : 0
            const custo = (roiObj.custo_estimado != null && roiObj.custo_estimado !== '') ? Number(roiObj.custo_estimado) : ((m && m.custo != null) ? Number(m.custo) : 0)
            const roi = custo > 0 && valor > 0 ? (((valor - custo) / custo) * 100).toFixed(1) : null
            this.$set(this.metricasPorCampanha, c.id, {
              valor_total: valor,
              custo,
              roi,
            })
          } catch (_) {
            this.$set(this.metricasPorCampanha, c.id, { valor_total: 0, custo: 0, roi: null })
          }
        }
        // Preencher totais se a API não retornou (fallback a partir das métricas carregadas)
        if ((this.estatisticasCampanhas.total_campanhas == null || this.estatisticasCampanhas.total_campanhas === '') && campanhasList.length > 0) {
          this.estatisticasCampanhas = { ...this.estatisticasCampanhas, total_campanhas: campanhasList.length }
        }
        if ((this.estatisticasCampanhas.valor_total_conversoes == null || this.estatisticasCampanhas.valor_total_conversoes === '') && Object.keys(this.metricasPorCampanha).length > 0) {
          const soma = Object.values(this.metricasPorCampanha).reduce((acc, x) => acc + (Number(x.valor_total) || 0), 0)
          this.estatisticasCampanhas = { ...this.estatisticasCampanhas, valor_total_conversoes: soma }
        }
      } catch (e) {
        console.error('Erro campanhas:', e)
        this.campanhasSegmentadas = []
        this.estatisticasCampanhas = {}
      } finally {
        this.loadingCampanhas = false
      }
    },
    async carregarCampanhasInteligentes() {
      const uuid = this.storeUuid
      if (!uuid) return
      this.loadingInteligentes = true
      try {
        const [statusRec, campanhasBday] = await Promise.all([
          verificarStatusRecomendacoes(uuid).catch(() => null),
          listarCampanhasAniversario(uuid).catch(() => []),
        ])
        this.statusRecomendacao = statusRec
        this.campanhasAniversario = campanhasBday || []
        this.campanhaAniversarioAtiva = (campanhasBday || []).find(c => c.is_active === 1) || null

        let metricasRec = null
        try {
          metricasRec = await buscarMetricasRecomendacoes(uuid, {
            start_date: this.periodoNotif.dataMin,
            end_date: this.periodoNotif.dataMax.split(' ')[0],
          })
        } catch (_) {}
        this.metricasRecomendacao = metricasRec

        this.metricasAniversario = null
        if (this.campanhaAniversarioAtiva) {
          try {
            const conv = await buscarMetricasConversaoAniversario(this.campanhaAniversarioAtiva.id, uuid)
            this.metricasAniversario = {
              total_sent: conv?.total_sent,
              conversions_count: conv?.conversions_count ?? conv?.total_conversoes,
              valor_total: conv?.valor_total ?? conv?.total_revenue ?? 0,
            }
          } catch (_) {}
        }
      } catch (e) {
        console.error('Erro campanhas inteligentes:', e)
      } finally {
        this.loadingInteligentes = false
      }
    },
    abrirModalTemplateNotif(n) {
      this.notifModalSelecionada = n
      this.modalTemplateNotifVisivel = true
    },
    /** Retorna true se existe notificação configurada com esse status_id ativa */
    notificacaoAtivaPorStatus(statusId) {
      if (statusId == null) return false
      return (this.notificacoesLista || []).some(n => n.status_id === statusId && n.ativo)
    },
    /** True se entre as últimas 10 notificações desse status houve falha de envio */
    ultimas10ComFalha(n) {
      if (!n || n.status_id == null) return false
      const list = this.historicoNotificacoesUnificado || []
      const doStatus = list.filter(item => item.status_id === n.status_id)
      const ultimas10 = doStatus.slice(0, 10)
      return ultimas10.some(r => r.envio === 'Não enviado')
    },
    nomeStatusNotif(statusId) {
      return STATUS_LIST[statusId] || STATUS_NOTIFICACOES.find(s => s.id === statusId)?.nome || `Status ${statusId}`
    },
    nomePaymentNotif(paymentId) {
      const found = Object.values(METODOS_PAGAMENTO || {}).find(p => p && p.id === paymentId)
      return found ? found.nome : `Pag. ${paymentId}`
    },
    parseDate(value) {
      if (value == null || value === '') return null
      if (typeof value === 'number') return Number.isNaN(value) ? null : value
      if (typeof value !== 'string') return null
      if (value.indexOf('T') !== -1) return new Date(value).getTime()
      const parts = String(value).replace(/\//g, '-').split(' ')
      const d = parts[0]
      const t = parts[1] || '00:00'
      const parsed = new Date(`${d}T${t}:00`).getTime()
      return Number.isNaN(parsed) ? null : parsed
    },
    formatarData(val) {
      if (val == null || val === '') return '-'
      const d = new Date(val)
      return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatarDataHora(val) {
      if (val == null || val === '') return '-'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return '-'
      return d.toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },
    campanhaFoiEnviada(c) {
      return !!(c && (c.last_sent_at || (c.send_date && this.parseDate(c.send_date) <= Date.now())))
    },
    formatarMoeda(val) {
      if (val == null || val === '' || Number.isNaN(Number(val))) return '-'
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(val))
    },
  },
}
</script>

<style lang="scss" scoped>
.painel-customer-success {
  padding: 0.5rem 0;
}
.cluster-card {
  border-left: 4px solid rgba(115, 103, 240, 0.3);
}
.campanhas-scroll-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  margin: 0 -0.25rem;
  padding: 0.25rem;
}
.campanhas-scroll {
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  min-width: min-content;
}
.campanha-card-scroll {
  flex: 0 0 280px;
  min-width: 280px;
  max-width: 280px;
}
</style>
